import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  startAfter,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../lib/firebase';

const PAGE_SIZE = 10;

export function mapProduct(docSnap) {
  const data = docSnap.data();
  return {
    uid: docSnap.id,
    id: data.id || docSnap.id,
    ...data,
  };
}

/** Lista productos del tenant con paginación real de Firestore. */
export async function listProducts({ ruc, pageSize = PAGE_SIZE, cursor = null, search = '', includeInactive = false, stockBajo = false }) {
  if (!ruc) throw new Error('RUC requerido');

  const constraints = [where('ruc', '==', ruc)];
  if (!includeInactive) {
    constraints.push(where('activo', '==', true));
  }

  // Firestore no soporta OR de texto; si hay búsqueda simple, filtramos por
  // prefijo de name/id cuando es posible, o bajamos una página y filtramos.
  if (search) {
    const q = query(collection(db, 'productos'), ...constraints, orderBy('name'), limit(pageSize * 3));
    const snap = await getDocs(q);
    const term = search.toLowerCase().trim();
    const items = snap.docs
      .map(mapProduct)
      .filter((p) =>
        String(p.name || '').toLowerCase().includes(term) ||
        String(p.id || '').toLowerCase().includes(term) ||
        String(p.codigoBarra || '').toLowerCase().includes(term)
      )
      .slice(0, pageSize);
    return { items, hasMore: snap.docs.length >= pageSize * 3 };
  }

  const q = cursor
    ? query(collection(db, 'productos'), ...constraints, orderBy('name'), startAfter(cursor), limit(pageSize))
    : query(collection(db, 'productos'), ...constraints, orderBy('name'), limit(pageSize));

  const snap = await getDocs(q);
  let items = snap.docs.map(mapProduct);

  if (stockBajo) {
    items = items.filter(
      (p) => p.controlaStock && Number(p.stock || 0) <= Number(p.stockMinimo || 0)
    );
  }

  const last = snap.docs[snap.docs.length - 1] || null;
  return { items, cursor: last, hasMore: snap.docs.length === pageSize };
}

export async function getProduct(uid) {
  const snap = await getDoc(doc(db, 'productos', uid));
  if (!snap.exists()) throw new Error('Producto no encontrado');
  return mapProduct(snap);
}

export async function createProduct({ ruc, data }) {
  if (!ruc) throw new Error('RUC requerido');
  const payload = {
    name: data.name,
    descripcion: data.descripcion || '',
    id: data.id || null,
    impuesto: Number(data.impuesto ?? 0.18),
    price: Number(data.price || 0),
    costo: Number(data.costo || 0),
    currency: data.currency || 'PEN',
    unidad: data.unidad || 'NIU',
    image: data.image || '',
    activo: data.activo !== false,
    controlaStock: !!data.controlaStock,
    controlaLotes: !!data.controlaLotes,
    codigoBarra: data.codigoBarra || null,
    marca: data.marca || '',
    modelo: data.modelo || '',
    ruc,
    creadoEn: new Date().toISOString(),
  };

  if (data.controlaStock) {
    payload.stock = parseInt(data.stock, 10) || 0;
    payload.stockMinimo = parseInt(data.stockMinimo, 10) || 0;
    payload.stockMaximo = parseInt(data.stockMaximo, 10) || 0;
  }

  const docRef = await addDoc(collection(db, 'productos'), payload);
  await updateDoc(docRef, { uid: docRef.id });

  if (data.controlaStock && data.controlaLotes && data.numeroLote && data.fechaVencimiento) {
    await addDoc(collection(db, `productos/${docRef.id}/lotes`), {
      numeroLote: String(data.numeroLote).trim(),
      cantidad: parseInt(data.stock, 10) || 0,
      fechaCaducidad: new Date(`${data.fechaVencimiento}T00:00:00`),
    });
  }

  return docRef.id;
}

export async function updateProduct(uid, data) {
  const payload = {
    name: data.name,
    descripcion: data.descripcion || '',
    id: data.id,
    impuesto: Number(data.impuesto ?? 0.18),
    price: Number(data.price || 0),
    costo: Number(data.costo || 0),
    currency: data.currency || 'PEN',
    unidad: data.unidad || 'NIU',
    image: data.image || '',
    activo: data.activo !== false,
    controlaStock: !!data.controlaStock,
    controlaLotes: !!data.controlaLotes,
    codigoBarra: data.codigoBarra || null,
    marca: data.marca || '',
    modelo: data.modelo || '',
    updatedAt: serverTimestamp(),
  };
  if (data.controlaStock) {
    payload.stock = parseInt(data.stock, 10) || 0;
    payload.stockMinimo = parseInt(data.stockMinimo, 10) || 0;
    payload.stockMaximo = parseInt(data.stockMaximo, 10) || 0;
  }
  await updateDoc(doc(db, 'productos', uid), payload);
}

export async function setProductActive(uid, activo) {
  await updateDoc(doc(db, 'productos', uid), { activo, updatedAt: serverTimestamp() });
}

export async function listLotes(productUid, pageSize = 20) {
  const snap = await getDocs(
    query(collection(db, `productos/${productUid}/lotes`), orderBy('fechaCaducidad', 'asc'), limit(pageSize))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function createLote(productUid, { numeroLote, cantidad, fechaCaducidad }) {
  return addDoc(collection(db, `productos/${productUid}/lotes`), {
    numeroLote: String(numeroLote || 'LOTE-GENERAL').trim(),
    cantidad: Number(cantidad || 0),
    fechaCaducidad: fechaCaducidad instanceof Date ? fechaCaducidad : new Date(fechaCaducidad),
  });
}

export async function listMovimientos({ ruc, productUid = null, pageSize = PAGE_SIZE }) {
  if (!ruc) throw new Error('RUC requerido');
  const constraints = [where('ruc', '==', ruc)];
  if (productUid) constraints.push(where('productoId', '==', productUid));
  const snap = await getDocs(
    query(collection(db, 'movimientos_stock'), ...constraints, orderBy('timestamp', 'desc'), limit(pageSize))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
